import { FC, useMemo, useRef, useState } from 'react'
import styles from './ProducerSelectModal.module.scss'
import { ParentProducer } from 'types/producer'
import Modal from 'components/modal/Modal'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import NewProducerMap from 'components/producers/new-producer-map/NewProducerMap'
import { NewProducerMapProps, PopulatedLocation } from 'components/producers/new-producer-map/types'
import { filterLocations } from 'features/producer-detail/utils'
import { CldImage } from 'next-cloudinary'
import { Heading } from '@boltenergy-be/design-system'

type Props = {
  producer: ParentProducer
  isOpen: boolean
  onRequestClose: () => void
  populatedLocations: PopulatedLocation[]
}

type HeaderProps = {
  avatar: string
  name: string
  onClose: () => void
}

const ProducerSelectModalHeader: FC<HeaderProps> = ({ avatar, name, onClose }) => {
  // i18n
  const { t } = useTranslation('producer')

  return (
    <header className={styles.header}>
      <CldImage className={styles.avatar} alt={name} src={avatar} width={60} height={60} placeholder="blur" blurDataURL={avatar} />

      <div className={styles.info}>
        <small>{t('producer', 'Energie-opwekker')}</small>
        <strong>{name}</strong>
      </div>

      <button onClick={onClose}>&times;</button>
    </header>
  )
}

const ProducerSelectModal: FC<Props> = ({ producer, populatedLocations, ...modalProps }) => {
  // Router
  const { locale } = useRouter()

  // i18n
  const { t } = useTranslation('producer')

  // Local state
  const [hideSoldOutLocations, setHideSoldOutLocations] = useState<boolean>(true)

  // Ref
  const mapRef = useRef<HTMLDivElement>(null)

  // Constants
  const filteredLocations = useMemo<NewProducerMapProps['locations']>(
    () => filterLocations(populatedLocations, hideSoldOutLocations),
    [hideSoldOutLocations, populatedLocations]
  )

  if (modalProps.isOpen) {
    return (
      <Modal
        {...modalProps}
        className={styles.modal}
        customHeader={
          <ProducerSelectModalHeader
            avatar={producer.images.profile.url}
            name={producer.name[locale]}
            onClose={modalProps.onRequestClose}
          />
        }
      >
        <div className={styles.content}>
          <Heading as="h1" variant="h3" className="mb-300">
            {t('modal.title', 'Kies een locatie om verder te gaan')}
          </Heading>
          <p>
            {t(
              'modal.description',
              'Deze energie-opwekker wekt stroom op op verschillende locaties. Kies een installatie in jouw buurt of één naar keuze.'
            )}
          </p>
          <form className={styles['toggle-wrapper']}>
            <label className="is-flex is-flex-row">
              <input type="checkbox" checked={hideSoldOutLocations} onChange={() => setHideSoldOutLocations(!hideSoldOutLocations)} />
              {t('map.hideSoldOut', 'Verberg uitverkochte locaties')}
            </label>
          </form>

          <div ref={mapRef}>
            <NewProducerMap
              locations={filteredLocations}
              height="470px"
              zoom={11}
              center={
                producer && {
                  lat: Number((filteredLocations.length > 0 ? filteredLocations : populatedLocations)[0]?.coordinates?.lat),
                  lng: Number((filteredLocations.length > 0 ? filteredLocations : populatedLocations)[0]?.coordinates?.long)
                }
              }
              enablePanning
              defaultOpenSlug={filteredLocations[0]?.slug}
            />
          </div>
        </div>
      </Modal>
    )
  }

  return null
}

export default ProducerSelectModal
